"use client";

import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";

type OrganizationLogoProps = {
  organizationName: string;
  logoUrl?: string | null;
  size?: "sm" | "md" | "lg";
  variant?: "icon" | "wordmark";
  className?: string;
};

const iconSizeClasses = {
  sm: "h-7 w-7 rounded-lg text-xs",
  md: "h-10 w-10 rounded-xl text-sm",
  lg: "h-28 w-28 rounded-2xl text-3xl",
} as const;

const wordmarkSizeClasses = {
  sm: "h-10 w-[4.75rem] rounded-xl text-xs",
  md: "h-12 w-[5.5rem] rounded-xl text-sm",
  lg: "h-28 w-52 rounded-2xl text-3xl",
} as const;

const iconDimensions = {
  sm: { width: 28, height: 28 },
  md: { width: 40, height: 40 },
  lg: { width: 112, height: 112 },
} as const;

const wordmarkDimensions = {
  sm: { width: 76, height: 40 },
  md: { width: 88, height: 48 },
  lg: { width: 208, height: 112 },
} as const;

const wordmarkImagePadding = {
  sm: "p-1",
  md: "p-1.5",
  lg: "p-2",
} as const;

export function OrganizationLogo({
  organizationName,
  logoUrl,
  size = "md",
  variant = "icon",
  className,
}: OrganizationLogoProps) {
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setHasImageError(false);
  }, [logoUrl]);

  const initial = useMemo(
    () => Array.from(organizationName.trim())[0] ?? "組",
    [organizationName]
  );
  const imageUrl = logoUrl && !hasImageError ? logoUrl : null;
  const showsLogoImage = Boolean(imageUrl);
  const showsWordmark = showsLogoImage && variant === "wordmark";
  const dimensions = showsWordmark
    ? wordmarkDimensions[size]
    : iconDimensions[size];

  return (
    <span
      className={cn(
        "border-border bg-card text-brand-strong inline-flex shrink-0 items-center justify-center overflow-hidden border font-bold shadow-sm",
        showsWordmark ? wordmarkSizeClasses[size] : iconSizeClasses[size],
        className
      )}
      role={showsLogoImage ? undefined : "img"}
      aria-label={showsLogoImage ? undefined : `${organizationName}のロゴ`}
    >
      {showsLogoImage ? (
        <img
          src={imageUrl ?? undefined}
          alt={`${organizationName}のロゴ`}
          width={dimensions.width}
          height={dimensions.height}
          className={cn(
            "h-full w-full object-contain",
            showsWordmark ? wordmarkImagePadding[size] : "p-1"
          )}
          onError={() => setHasImageError(true)}
        />
      ) : (
        <span aria-hidden="true">{initial}</span>
      )}
    </span>
  );
}
